import React from "react";
import clsx from "clsx";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import ArrowForwardIosOutlinedIcon from "@mui/icons-material/ArrowForwardIosOutlined";
import Link from "next/link";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  basePath: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  basePath,
}) => {
  if (totalPages <= 1) return null;

  const getPages = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 3;

    if (totalPages <= maxVisible + 2) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (currentPage <= maxVisible) {
      pages.push(
        ...Array.from({ length: maxVisible }, (_, i) => i + 1),
        "...",
        totalPages
      );
    } else if (currentPage > totalPages - maxVisible) {
      pages.push(
        1,
        "...",
        ...Array.from(
          { length: maxVisible },
          (_, i) => totalPages - maxVisible + 1 + i
        )
      );
    } else {
      pages.push(
        1,
        "...",
        currentPage - 1,
        currentPage,
        currentPage + 1,
        "...",
        totalPages
      );
    }

    return pages;
  };

  const getHref = (page: number) =>
    page === 1 ? `${basePath}` : `${basePath}?page=${page}`;

  return (
    <div className="flex flex-col items-center gap-2.5 mt-10">
      <nav className="flex items-center justify-center gap-1">
        {/* Previous */}
        {currentPage > 1 ? (
          <Link
            href={getHref(currentPage - 1)}
            className="flex cursor-pointer md:mr-3"
          >
            <ArrowBackIosNewOutlinedIcon fontSize="medium" />
          </Link>
        ) : (
          <span className="flex opacity-50 cursor-default md:mr-3">
            <ArrowBackIosNewOutlinedIcon fontSize="medium" />
          </span>
        )}
        {/* Pages */}
        {getPages().map((page, idx) =>
          page === "..." ? (
            <div
              key={`ellipsis-${idx}`}
              className="text-xl opacity-50 w-10 h-10 flex justify-center"
            >
              ...
            </div>
          ) : (
            <Link
              key={page}
              href={getHref(page as number)}
              className={clsx(
                "relative w-10 h-10 flex items-center justify-center text-xl rounded-full cursor-pointer transition-opacity duration-200 ease-in-out",
                {
                  "bg-tm-c-brand text-white opacity-100 !cursor-default":
                    currentPage === page,
                  "text-white opacity-50 hover:!opacity-100 hover:!bg-tm-c-brand/20":
                    currentPage !== page,
                }
              )}
            >
              {page}
            </Link>
          )
        )}
        {/* Next */}
        {currentPage < totalPages ? (
          <Link
            href={getHref(currentPage + 1)}
            className="flex cursor-pointer md:ml-3"
            prefetch
          >
            <ArrowForwardIosOutlinedIcon fontSize="medium" />
          </Link>
        ) : (
          <span className="flex opacity-50 cursor-default md:ml-3">
            <ArrowForwardIosOutlinedIcon fontSize="medium" />
          </span>
        )}
      </nav>
      {/* Page info */}
      <p className="text-sm">
        <span>Page {currentPage}</span>{" "}
        <span className="opacity-50">of {totalPages}</span>
      </p>
    </div>
  );
};
